import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHotkeys } from 'react-hotkeys-hook'
import { toast } from 'sonner'
import { v7 as uuidv7 } from 'uuid'
import { Plus } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import { useStatuses } from '../hooks/useWorkspaceStatuses'
import { useProjects } from '../hooks/useProjects'
import { resolveTaskIdentifier } from '../utils/taskIdentifier'
import { useTaskListNavigation } from '../hooks/useTaskListNavigation'
import { countParsedTokens, parseQuickAddTokens } from '../utils/quickAddTokens'
import { BulkActionBar } from './BulkActionBar'
import TaskDetailsPanel from './TaskDetailsPanel'
import { TaskDetailsDialog } from './TaskDetailsDialog'
import { TaskList, formatTaskDueDate } from './TaskList'
import { AddTaskInput } from "./AddTaskInput"
import type { ReactNode } from 'react'

import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import type { LabelGroupDocument } from '@/lib/db/rxdb/schemas/labelGroup'
import type { RecurrencePattern } from '@/lib/types/recurrence'
import type { TaskReorderEvent } from './TaskList'
import { LayoutButton } from '@/routes/features/projects/components/LayoutButton'
import { cn } from '@/lib/utils'
import { buildPlainTextEditorStateJSON } from '@/components/editor/TiptapRichTextEditor'
import {
  compareRanks,
  generateRankBetween,
} from '@/lib/utils/lexorank'
import { rebalanceSortOrders } from '@/lib/utils/sortOrder'
import { createTaskWithLabels } from '@/lib/sync/taskActions'
import {
  useReminderMutations,
  useTask,
  useTaskContentMutations,
  useTaskLabelMutations,
  useTaskMutations,
} from '@/lib/db/rxdb/hooks'
import { calculateNextScheduledAt } from '@/lib/utils/reminderCalculations'
import useLoginStore from '@/store/LoginStore'
import { useProjectStatusMode } from '@/hooks/useProjectStatusMode'
import useViewModeStore from '@/store/ViewModeStore'
import { useQuickAddStore } from '@/store/QuickAddStore'
import { Button } from '@/components/ui/button'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import { useIsCompactDesktop, useIsMobile } from '@/hooks/useMobile'
import { PhoneMenuButton } from '@/components/app/layout/PhoneMenuButton'
import { useAllLabels } from '@/lib/hooks/useLabels'
import { useAllLabelGroups } from '@/lib/hooks/useLabelGroups'
import {
  confirmUpload,
  getPresignedUploadUrl,
} from '@/lib/api/features/attachments'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { PROGRESS_TYPES } from '@/lib/types/subscription'
import { useWorkItemTypesForProject } from '@/lib/db/rxdb/hooks/entities/work-item-types'
import { useProject } from '@/lib/db/rxdb/hooks/entities/projects'
import { useMembers } from '@/lib/db/rxdb/hooks/entities/members'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { resolveDefaultWorkItemTypeId } from '@/lib/types/workItemType'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { getUploadContentType } from '@/lib/utils/fileMime'


/**
 * Context object passed to custom task view renderers.
 * Contains all necessary data and callbacks for rendering task lists.
 */
export type TaskViewContext = {
  /** Array of tasks to display */
  items: Array<TaskDocument>
  /** Whether data is currently loading */
  isLoading: boolean
  /** Default reorder handler for drag-and-drop */
  defaultOnReorder?: (event: TaskReorderEvent) => void
  /** Callback when a task is selected */
  onSelect: (task: TaskDocument) => void
  /** Currently selected task ID */
  selectedTaskId: string | null
  /** CSS class for task container */
  containerClassName: string
  /** Inline styles for task container */
  containerStyle?: React.CSSProperties
  /** CSS class for individual task items */
  itemClassName?: string
  /** Function to format due dates for display */
  formatDueDate: (value: TaskDocument['dueDate']) => string
  /** Current view mode (simple or split) */
  viewMode: 'simple' | 'split'
  /** Open quick-add prefilled for a group (design 18b); absent where there's no quick-add. */
  openAddTask?: (preset: { dueDate?: Date | null; statusId?: string }) => void
}

/**
 * Props for the TaskPageLayout component.
 */
interface TaskPageLayoutProps {
  /** Array of tasks to display */
  tasks: Array<TaskDocument>
  /** Whether tasks are currently loading */
  isLoading: boolean
  /** Default due date for new tasks */
  defaultDueDate?: Date
  /** Whether new tasks should default to "someday" */
  defaultIsSomeday?: boolean
  /** Custom renderer for task view */
  renderTaskView?: (context: TaskViewContext) => ReactNode
  /** Function to transform/sort items before display */
  transformItems?: (items: Array<TaskDocument>) => Array<TaskDocument>
  /** Whether to show add task input */
  showAddTaskInput?: boolean
  /** Whether to show view mode toggle */
  showViewModeToggle?: boolean
  /** Page title for display */
  pageTitle?: string
  /** Whether clicking subtasks navigates to detail page vs updating selection */
  navigateToSubtasks?: boolean
  /** Project ID to lock new tasks to (used when adding tasks from project view) */
  projectId?: string
  /** Project ID to default new tasks to without locking selection */
  defaultProjectId?: string | null
  /** Override the view mode instead of reading from the global ViewModeStore.
   * Used by project list view to source split/simple from per-project preferences. */
  viewModeOverride?: 'simple' | 'split'
  /** Replaces the plain task count after the title, e.g. "5 · 1 overdue" (design 34a). */
  headerCount?: ReactNode
  /** Extra controls in the title bar, before Display (design 34a: List / Week). */
  headerActions?: ReactNode
  /** Use the full width instead of the reading column (design 34b's Week board). */
  wide?: boolean
  /**
   * A list that has never had a task (design 33a): no detail pane, the add
   * row itself is the empty state — open and focused — with this intro above
   * it and tips below.
   */
  emptyIntro?: TaskEmptyIntro
  /** What an empty list says instead of rows (design 33b). */
  renderEmptyState?: (context: TaskEmptyContext) => ReactNode
}

export type TaskEmptyContext = {
  /** Opens and focuses the add row. */
  openAddTask: () => void
}

export type TaskEmptyIntro = {
  title: ReactNode
  description: ReactNode
  renderTips?: (context: TaskEmptyContext) => ReactNode
  footer?: ReactNode
}

/**
 * Main layout component for task management pages.
 * 
 * Provides a comprehensive interface for viewing and managing tasks with:
 * - Flexible view modes (simple list or split view with details panel)
 * - Drag-and-drop reordering with lexicographic rank management
 * - Task creation with rich input options (dates, projects, labels)
 * - Real-time task filtering and transformation
 * - Responsive design with resizable panels
 * - Keyboard navigation and shortcuts
 * 
 * Features:
 * - Split view with resizable panels for list + details
 * - Simple view with modal/dialog details
 * - Custom task view renderers for specialized layouts
 * - Add task input with smart defaults
 * - View mode persistence and toggle
 * - File attachment support with drag-and-drop
 * - Real-time sync with optimistic updates
 * 
 * @param props - TaskPageLayout configuration options
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <TaskPageLayout
 *   tasks={tasks}
 *   isLoading={isLoading}
 *   pageTitle="Inbox"
 * />
 * 
 * // With custom renderer and transformation
 * <TaskPageLayout
 *   tasks={tasks}
 *   isLoading={isLoading}
 *   transformItems={(items) => items.sort(customSort)}
 *   renderTaskView={(context) => <CustomTaskList {...context} />}
 *   defaultDueDate={tomorrow}
 * />
 * ```
 */
export function TaskPageLayout({
  tasks,
  isLoading,
  defaultDueDate,
  defaultIsSomeday,
  renderTaskView,
  transformItems,
  showAddTaskInput = true,
  showViewModeToggle = true,
  pageTitle,
  navigateToSubtasks = false,
  projectId,
  defaultProjectId,
  viewModeOverride,
  headerCount,
  headerActions,
  wide = false,
  emptyIntro,
  renderEmptyState,
}: TaskPageLayoutProps) {
  const {
    all: { statuses },
    isLoading: statusesLoading,
  } = useStatuses()
  const { projects, defaultProject, isLoading: projectsLoading } = useProjects()
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const { labels, isLoading: labelsLoading } = useAllLabels(
    workspaceId,
  )
  const { labelGroups, isLoading: labelGroupsLoading } = useAllLabelGroups(
    workspaceId,
  )

  return (
    <>
    <TaskPageLayoutContent
      tasks={tasks}
      isLoading={
      isLoading ||
      statusesLoading ||
      projectsLoading ||
      labelsLoading ||
      labelGroupsLoading
      }
      statuses={statuses}
      projects={projects}
      defaultProject={defaultProject}
      labels={labels}
      labelGroups={labelGroups}
      defaultDueDate={defaultDueDate}
      defaultIsSomeday={defaultIsSomeday}
      renderTaskView={renderTaskView}
      transformItems={transformItems}
      showAddTaskInput={showAddTaskInput}
      showViewModeToggle={showViewModeToggle}
      pageTitle={pageTitle}
      navigateToSubtasks={navigateToSubtasks}
      projectId={projectId}
      defaultProjectId={defaultProjectId}
      viewModeOverride={viewModeOverride}
      headerCount={headerCount}
      headerActions={headerActions}
      wide={wide}
      emptyIntro={emptyIntro}
      renderEmptyState={renderEmptyState}
    />
    <BulkActionBar />
    </>
  )
}

function TaskPageLayoutContent({
  tasks,
  isLoading: isTaskListLoading,
  statuses,
  projects,
  defaultProject,
  labels,
  labelGroups,
  defaultDueDate,
  defaultIsSomeday,
  renderTaskView,
  transformItems,
  showAddTaskInput,
  showViewModeToggle,
  pageTitle,
  navigateToSubtasks = false,
  projectId,
  defaultProjectId,
  viewModeOverride,
  headerCount,
  headerActions,
  wide = false,
  emptyIntro,
  renderEmptyState,
}: TaskPageLayoutProps & {
  statuses: Array<StatusDocument>
  projects: Array<ProjectDocument>
  defaultProject: ProjectDocument | null
  labels: Array<LabelDocument>
  labelGroups: Array<LabelGroupDocument>
}) {
  const { t } = useTranslation('tasks')
  const isMobile = useIsMobile()
  const isCompactDesktop = useIsCompactDesktop()
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const routerState = useRouterState()
  const workspaceSlug = params.workspaceSlug
  const { updateTask, createTask } = useTaskMutations()
  const { createReminder } = useReminderMutations()
  const { createTaskLabel } = useTaskLabelMutations()
  const { createTaskContent } = useTaskContentMutations()
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)

  // Check if project uses advanced status management
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const { statusMode, isAdvanced: canUseAdvanced } = useProjectStatusMode(selectedProjectId)

  // Work item types available for the selected project
  const { workItemTypes: projectWorkItemTypes } = useWorkItemTypesForProject(selectedProjectId)
  const [selectedWorkItemTypeId, setSelectedWorkItemTypeId] = useState<string | null>(null)
  const { isPersonal } = useWorkspaceMode()
  const { project: selectedProject } = useProject(selectedProjectId ?? undefined)
  const isWorkItemTypeEnabled =
    FEATURE_FLAGS.ENABLE_WORK_ITEM_TYPES && !isPersonal && (selectedProject?.isWorkItemTypeEnabled ?? false)

  useEffect(() => {
    setSelectedWorkItemTypeId(
      isWorkItemTypeEnabled
        ? resolveDefaultWorkItemTypeId(projectWorkItemTypes)
        : null,
    )
  }, [selectedProjectId, isWorkItemTypeEnabled, projectWorkItemTypes])

  const [startDate, setStartDate] = useState<Date | undefined>()
  const [dueDate, setDueDate] = useState<Date | undefined>(defaultDueDate)
  const [reminderOffsetMinutes, setReminderOffsetMinutes] = useState<
    number | null
  >(null)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [selectedStatusId, setSelectedStatusId] = useState<string | null>(null)
  const [selectedPriority, setSelectedPriority] = useState<number>(0)
  // null = the default assignee (you); '' = explicitly unassigned.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [isSomeday, setIsSomeday] = useState(defaultIsSomeday ?? false)
  const [isAllDay, setIsAllDay] = useState(true)
  const [description, setDescription] = useState('')
  const [taskName, setTaskName] = useState('')
  const [selectedLabelIds, setSelectedLabelIds] = useState<Array<string>>([])
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([])
  const [recurrencePattern, setRecurrencePattern] = useState<
    RecurrencePattern | null
  >(null)
  const [isAddTaskExpanded, setIsAddTaskExpanded] = useState(false)
  // Tokens typed into the row that became properties ("N parsed", design 25a).
  const [parsedCount, setParsedCount] = useState(0)
  // Rank of the last task added while the row stayed open, so a burst of
  // adds lands in the order typed, just under the row.
  const burstAnchorRef = React.useRef<string | null>(null)
  const [formKey, setFormKey] = useState(0)
  const taskInputElementRef = React.useRef<HTMLInputElement>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const storeViewMode = useViewModeStore((s) => s.viewMode)
  const baseViewMode = viewModeOverride ?? storeViewMode
  const resolvedDefaultProjectId = useMemo(() => {
    if (projectId) {
      return projectId
    }

    if (defaultProjectId) {
      return defaultProjectId
    }

    return defaultProject?.id ?? null
  }, [defaultProject?.id, defaultProjectId, projectId])

  useEffect(() => {
    setIsSomeday(defaultIsSomeday ?? false)
  }, [defaultIsSomeday])

  useEffect(() => {
    if (isAddTaskExpanded && taskInputElementRef.current) {
      taskInputElementRef.current.focus()
    }
  }, [isAddTaskExpanded])

  const statusList = useMemo<Array<StatusDocument>>(() => {
    return statuses
  }, [statuses])

  const orderedTasks = useMemo<Array<TaskDocument>>(() => {
    const orderMap = new Map<string, number>()
    tasks.forEach((item, index) => {
      orderMap.set(item.id, index)
    })

    return [...tasks].sort((a, b) => {
      const rankComparison = compareRanks(a.sortOrder, b.sortOrder)
      if (rankComparison !== 0) {
        return rankComparison
      }

      return (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
    })
  }, [tasks])

  const displayTasks = useMemo<Array<TaskDocument>>(() => {
    if (!transformItems) {
      return orderedTasks
    }

    const transformed = transformItems(orderedTasks)
    return Array.isArray(transformed) ? transformed : orderedTasks
  }, [orderedTasks, transformItems])

  const isEmpty = !isTaskListLoading && displayTasks.length === 0
  const showEmptyIntro = isEmpty && !!emptyIntro && showAddTaskInput
  // Nothing to select, so no detail pane (design 33a); split view comes back
  // with the first task (18a).
  const viewMode = showEmptyIntro ? 'simple' : baseViewMode
  useEffect(() => {
    if (!showEmptyIntro) return
    setIsAddTaskExpanded(true)
    // The row mounts (and permissions resolve) after this render, so focus
    // once it's there — the add row is the empty state and starts focused.
    let tries = 0
    const timer = window.setInterval(() => {
      const input = taskInputElementRef.current
      tries += 1
      if (input && !input.disabled) {
        input.focus()
        window.clearInterval(timer)
      } else if (tries > 20) {
        window.clearInterval(timer)
      }
    }, 50)
    return () => window.clearInterval(timer)
  }, [showEmptyIntro])

  useEffect(() => {
    if (displayTasks.length === 0) {
      setSelectedTaskId(null)
      return
    }

    setSelectedTaskId((current) => {
      if (current && displayTasks.some((item) => item.id === current)) {
        return current
      }

      return displayTasks[0]?.id ?? null
    })
  }, [displayTasks])

  // Fetch the selected task by ID (works for both parent tasks and sub-tasks)
  const { task: fetchedSelectedTask } = useTask(selectedTaskId ?? undefined)

  // Use the fetched task if available, otherwise fall back to searching displayTasks
  // Ensure selected task is null when there are no display tasks
  const selectedTask = useMemo(
    () => {
      if (displayTasks.length === 0) {
        return null
      }

      return fetchedSelectedTask ??
        (selectedTaskId
          ? (displayTasks.find((item) => item.id === selectedTaskId) ?? null)
          : null)
    },
    [fetchedSelectedTask, selectedTaskId, displayTasks],
  )

  const selectedParentTask = useMemo(() => {
    if (!selectedTask?.parentId) {
      return null
    }

    return (
      tasks.find(
        (item) =>
          item.id === selectedTask.parentId &&
          !item.isDeleted &&
          (workspaceId ? item.envId === workspaceId : true),
      ) ?? null
    )
  }, [selectedTask?.parentId, tasks, workspaceId])

  // Compute task identifier for the selected task
  const selectedTaskProject = useMemo(() => {
    if (!selectedTask?.projectId) {
      return null
    }
    return projects.find((p) => p.id === selectedTask.projectId) ?? null
  }, [selectedTask?.projectId, projects])

  const taskIdentifier = useMemo(
    () => resolveTaskIdentifier(selectedTask, selectedTaskProject?.projectKey),
    [selectedTask, selectedTaskProject?.projectKey],
  )

  // Navigation handlers for dialog
  const { handleNavigateUp, handleNavigateDown, canNavigateUp, canNavigateDown } =
    useTaskListNavigation(displayTasks, selectedTaskId, setSelectedTaskId)
  const selectedPosition = useMemo(() => {
    const index = displayTasks.findIndex((task) => task.id === selectedTaskId)
    return index < 0 ? undefined : { index: index + 1, total: displayTasks.length }
  }, [displayTasks, selectedTaskId])

  useEffect(() => {
    if (statusList.length === 0) {
      setSelectedStatusId(null)
      return
    }

    setSelectedStatusId((current) => {
      if (
        current &&
        statusList.some((status) => status.id === current)
      ) {
        return current
      }

      // In simplified mode, default to Backlog status
      if (!canUseAdvanced) {
        const backlogStatus = statusList.find(
          (status) => status.progressType === PROGRESS_TYPES.BACKLOG,
        )
        return backlogStatus?.id ?? statusList[0]?.id ?? null
      }

      // In advanced mode, default to first status in the list
      return statusList[0]?.id ?? null
    })
  }, [statusList, canUseAdvanced])

  useEffect(() => {
    if (projects.length === 0) {
      setSelectedProjectId(resolvedDefaultProjectId)
      return
    }

    setSelectedProjectId((current) => {
      if (projectId) {
        return resolvedDefaultProjectId
      }

      if (current && projects.some((project) => project.id === current)) {
        return current
      }

      return resolvedDefaultProjectId
    })
  }, [projects, projectId, resolvedDefaultProjectId])

  const handleDatePickerChange = (
    type: 'start' | 'due',
    date: Date | undefined,
  ) => {
    if (type === 'start') {
      setStartDate(date)
      // Clear someday when setting a start date
      if (date && isSomeday) {
        setIsSomeday(false)
      }
    } else {
      setDueDate(date)
      // Clear someday when setting a due date
      if (date && isSomeday) {
        setIsSomeday(false)
      }
    }
  }

  const handleAddTaskClick = useCallback(() => {
    setIsAddTaskExpanded(true)
    taskInputElementRef.current?.focus()
  }, [])

  // Only one "C" (design 25a): while page header controls are mounted they
  // own the hotkey and call back into this row; task pages have none.
  const registerInlineAdd = useQuickAddStore((s) => s.registerInline)
  const hasHeaderControls = useQuickAddStore((s) => s.controlIds.length > 0)
  useEffect(() => {
    if (!showAddTaskInput) return
    return registerInlineAdd(handleAddTaskClick)
  }, [showAddTaskInput, registerInlineAdd, handleAddTaskClick])

  const labelCandidates = useMemo(
    () => labels.filter((label) => !label.isDeleted),
    [labels],
  )
  // Only projects the picker offers (children of a space); none when the list
  // is locked to one project.
  const projectCandidates = useMemo(
    () => (projectId ? [] : projects.filter((project) => !!project.parentProjectId)),
    [projects, projectId],
  )

  const { members } = useMembers()
  const memberCandidates = useMemo(
    () =>
      isPersonal
        ? []
        : members.filter((member) => !member.isDeleted && !member.isSuperAgent),
    [members, isPersonal],
  )

  // Typed tokens fill the property chips (designs 18b, 25a, 29a): "!high",
  // "@me", "@dana", "#label", "@project" and a trailing date like "tomorrow ".
  const handleTaskNameChange = useCallback(
    (value: string) => {
      const parsed = parseQuickAddTokens(value, {
        labels: labelCandidates,
        members: memberCandidates,
        projects: projectCandidates,
        parseDates: true,
      })
      if (parsed.priority !== undefined) setSelectedPriority(parsed.priority)
      if (parsed.assignMe && userId) setSelectedUserId(userId)
      if (parsed.assigneeUserId) setSelectedUserId(parsed.assigneeUserId)
      const parsedLabelIds = parsed.labelIds
      if (parsedLabelIds) {
        setSelectedLabelIds((current) => [
          ...current,
          ...parsedLabelIds.filter((id) => !current.includes(id)),
        ])
      }
      if (parsed.projectId) setSelectedProjectId(parsed.projectId)
      if (parsed.dueDate) {
        // A date without a time is all-day, stored end-of-day like the picker.
        const due = new Date(parsed.dueDate)
        if (!parsed.dueHasTime) due.setHours(23, 59, 0, 0)
        setDueDate(due)
        setIsAllDay(!parsed.dueHasTime)
        setIsSomeday(false)
      }
      const count = countParsedTokens(parsed)
      if (count > 0) setParsedCount((current) => current + count)
      setTaskName(parsed.name)
    },
    [userId, labelCandidates, memberCandidates, projectCandidates],
  )

  useHotkeys('c', handleAddTaskClick, {
    enabled: showAddTaskInput && !isAddTaskExpanded && !hasHeaderControls,
    preventDefault: true,
  })

  const handleCancelAddTask = useCallback(() => {
    setIsAddTaskExpanded(false)
    setParsedCount(0)
    burstAnchorRef.current = null
    setTaskName('')
    setSelectedProjectId(resolvedDefaultProjectId)
    setStartDate(undefined)
    setDueDate(defaultDueDate)
    setReminderOffsetMinutes(null)
    setRecurrencePattern(null)
    setIsSomeday(defaultIsSomeday ?? false)
    setDescription('')
    setSelectedLabelIds([])
    setSelectedFiles([])
    setSelectedUserId(null)
    setSelectedWorkItemTypeId(null)
    setSelectedPriority(0)
    setIsAllDay(true)
  }, [defaultDueDate, defaultIsSomeday, resolvedDefaultProjectId])

  /** Enter keeps the row open for the next task; Shift+Enter opens the new one. */
  const createTaskHandler = useCallback(async (options: { open?: boolean } = {}) => {
    const taskTitle = taskName.trim()

    if (!taskTitle) {
      toast.error(t('quickCreate.nameRequired'))
      return
    }

    if (!workspaceId) {
      logger.logError(
        new Error('Cannot create task: Missing workspaceId'),
        LogCategory.TASK_CREATE,
        {
          component: 'TaskPageLayout',
          workspaceId,
        }
      )
      return
    }

    if (!selectedStatusId) {
      logger.logError(
        new Error('Cannot create task: No status selected'),
        LogCategory.TASK_CREATE,
        {
          component: 'TaskPageLayout',
          availableStatusesCount: statusList.length,
        }
      )
      return
    }

    if (!userId) {
      logger.logError(
        new Error('Cannot create task: Missing userId'),
        LogCategory.TASK_CREATE,
        {
          component: 'TaskPageLayout',
        }
      )
      return
    }

    try {
      const taskId = uuidv7()
      // The row sits at the top of the list, so the task lands there too
      // (design 25a): first, or just after the one added before it.
      const anchor = burstAnchorRef.current
      const anchorIndex = anchor
        ? orderedTasks.findIndex((item) => item.sortOrder === anchor)
        : -1
      const nextSortOrder =
        anchorIndex >= 0
          ? generateRankBetween(
              anchor,
              orderedTasks[anchorIndex + 1]?.sortOrder ?? null,
            )
          : generateRankBetween(null, orderedTasks[0]?.sortOrder ?? null)

      // Description is already in Tiptap JSON format from the editor
      // If it's empty, use empty editor state
      const editorJson = description || buildPlainTextEditorStateJSON('')

      // Extract plain text from editor JSON for search/display
      let plainText = ''
      try {
        const parsed = JSON.parse(editorJson)
        const textNode = parsed.content?.[0]?.content?.[0]
        plainText = textNode?.text || ''
      } catch {
        plainText = ''
      }

      // Note: newTask includes description fields for content creation (not part of TaskDocument)
      const newTask = {
        id: taskId,
        name: taskTitle,
        description: editorJson,
        descriptionFormat: 'tiptap',
        descriptionLength: editorJson.length,
        descriptionPlainText: plainText || null,
        sortOrder: nextSortOrder,
        projectId: selectedProjectId,
        // Assignee resolution: AssigneeComboBox emits '' for the
        // "no assignee" option, null/undefined when never set. Treat ''
        // as explicit Unassigned, null as "default to me". Mirrors the
        // mobile rule in useTaskCreation.ts.
        userId:
          selectedUserId === ''
            ? null
            : (selectedUserId ?? userId),
        startDate: startDate ? startDate.getTime() : null,
        startDateType: startDate ? (isAllDay ? 'date' : 'datetime') : null,
        dueDate: dueDate ? dueDate.getTime() : null,
        dueDateType: dueDate ? (isAllDay ? 'date' : 'datetime') : null,
        priority: selectedPriority,
        isSomeday: isSomeday,
        isAllDay: isAllDay,
        statusId: selectedStatusId,
        workItemTypeId: selectedWorkItemTypeId || null,
        parentId: null,
        statusName: '',
        statusColor: '#gray',
      }

      // Use optimistic action for multi-collection mutation
      await createTaskWithLabels({
        task: newTask,
        labelIds: selectedLabelIds,
        createTask: createTask,
        createTaskLabel: createTaskLabel,
        createTaskContent: createTaskContent,
        labels,
        labelGroups,
      })

      // Create reminder if one was set
      if (reminderOffsetMinutes !== null && dueDate && userId) {
        const dueDateMs = dueDate.getTime()
        const startDateMs = startDate ? startDate.getTime() : null
        const nextScheduledAt = calculateNextScheduledAt(
          1, // Relative to due_date
          null,
          reminderOffsetMinutes,
          dueDateMs,
          startDateMs,
          null,
        )

        void createReminder({
          id: crypto.randomUUID(),
          taskId: taskId,
          reminderType: 1, // 1 = Relative to due_date
          absoluteDate: null,
          relativeOffsetMinutes: reminderOffsetMinutes,
          notifyUserId: userId,
          // Aligned with mobile (useTaskCreation.ts): both clients now write
          // 'push' so the reminder router doesn't have to handle both values.
          notificationChannel: 'push',
          isEnabled: true,
          isSent: false,
          sentAt: null,
          // Recurrence fields from pattern
          recurrenceType: recurrencePattern?.recurrenceType ?? null,
          recurrenceInterval: recurrencePattern?.recurrenceInterval ?? null,
          recurrenceDaysOfWeek:
            recurrencePattern?.recurrenceDaysOfWeek ?? null,
          recurrenceDayOfMonth:
            recurrencePattern?.recurrenceDayOfMonth ?? null,
          recurrenceEndDate: recurrencePattern?.recurrenceEndDate
            ? new Date(recurrencePattern.recurrenceEndDate).getTime()
            : null,
          recurrenceMaxOccurrences:
            recurrencePattern?.recurrenceMaxOccurrences ?? null,
          occurrenceCount: 0,
          lastSentAt: null,
          nextScheduledAt,
        })
      }

      // Upload attachments if any
      if (selectedFiles.length > 0 && workspaceId) {
        // Upload files in the background (don't block task creation)
        void Promise.all(
          selectedFiles.map(async (file) => {
            try {
              const uploadContentType = getUploadContentType(file)
              // Get presigned URL
              const presignedData = await getPresignedUploadUrl(
                workspaceId,
                ENTITY_TYPES.WORK_ITEM,
                taskId,
                {
                  fileName: file.name,
                  fileType: uploadContentType,
                  fileSizeBytes: file.size,
                },
              )

              // Upload to R2
              const uploadResponse = await fetch(presignedData.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                  'Content-Type': uploadContentType,
                },
              })

              if (!uploadResponse.ok) {
                throw new Error(`Upload failed: ${uploadResponse.statusText}`)
              }

              // Confirm upload
              await confirmUpload(
                workspaceId,
                presignedData.attachmentId,
              )
            } catch (error) {
              // Don't fail task creation if attachment upload fails
            }
          }),
        )
      }

      if (options.open) {
        // Add & open: the row closes and the new task opens.
        setIsAddTaskExpanded(false)
        burstAnchorRef.current = null
        // Simple view, or split on mobile, shows details in the dialog.
        if (viewMode === 'simple' || isMobile) {
          setIsDialogOpen(true)
        }
      } else {
        burstAnchorRef.current = nextSortOrder
      }

      // Always reset the form fields
      setParsedCount(0)
      setSelectedPriority(0)
      setTaskName('')
      setStartDate(undefined)
      setDueDate(defaultDueDate)
      setReminderOffsetMinutes(null)
      setRecurrencePattern(null)
      setIsSomeday(defaultIsSomeday ?? false)
      setIsAllDay(true)
      setDescription('')
      setSelectedLabelIds([])
      setSelectedFiles([])
      setSelectedProjectId(resolvedDefaultProjectId)
      setSelectedUserId(null)
      setSelectedWorkItemTypeId(null)
      setSelectedTaskId(taskId)

      // Force editor to remount by changing the key
      setFormKey((prev) => prev + 1)

      // Keep going: refocus the (remounted) row for the next task
      if (!options.open) {
        setTimeout(() => {
          taskInputElementRef.current?.focus()
        }, 0)
      }
    } catch (error) {
      // Error creating task
    }
  }, [
    taskName,
    workspaceId,
    selectedStatusId,
    userId,
    orderedTasks,
    description,
    selectedProjectId,
    startDate,
    dueDate,
    selectedPriority,
    selectedUserId,
    isSomeday,
    isAllDay,
    reminderOffsetMinutes,
    recurrencePattern,
    defaultDueDate,
    defaultIsSomeday,
    selectedLabelIds,
    selectedFiles,
    selectedWorkItemTypeId,
    statusList,
    createTask,
    createTaskLabel,
    createTaskContent,
    createReminder,
    updateTask,
    resolvedDefaultProjectId,
    viewMode,
    isMobile,
    t,
  ])

  // "Paste a list" (design 33a): one task per line, in the order pasted, at
  // the top of the list, with whatever the row has set (project, status, due).
  const createTasksFromLines = useCallback(
    async (lines: Array<string>) => {
      if (!workspaceId || !userId || !selectedStatusId) return
      let before: string | null = null
      const after = orderedTasks[0]?.sortOrder ?? null
      try {
        for (const line of lines) {
          const sortOrder = generateRankBetween(before, after)
          before = sortOrder
          const editorJson = buildPlainTextEditorStateJSON('')
          await createTaskWithLabels({
            task: {
              id: uuidv7(),
              name: line.slice(0, 500),
              description: editorJson,
              descriptionFormat: 'tiptap',
              descriptionLength: editorJson.length,
              descriptionPlainText: null,
              sortOrder,
              projectId: selectedProjectId,
              userId: selectedUserId === '' ? null : (selectedUserId ?? userId),
              startDate: startDate ? startDate.getTime() : null,
              startDateType: startDate ? (isAllDay ? 'date' : 'datetime') : null,
              dueDate: dueDate ? dueDate.getTime() : null,
              dueDateType: dueDate ? (isAllDay ? 'date' : 'datetime') : null,
              priority: selectedPriority,
              isSomeday,
              isAllDay,
              statusId: selectedStatusId,
              workItemTypeId: selectedWorkItemTypeId || null,
              parentId: null,
              statusName: '',
              statusColor: '#gray',
            },
            labelIds: selectedLabelIds,
            createTask,
            createTaskLabel,
            createTaskContent,
            labels,
            labelGroups,
          })
        }
        toast.success(t('quickAdd.pasted', { count: lines.length }))
        setTaskName('')
      } catch {
        toast.error(t('quickAdd.pasteFailed'))
      }
    },
    [
      workspaceId,
      userId,
      selectedStatusId,
      orderedTasks,
      selectedProjectId,
      selectedUserId,
      startDate,
      dueDate,
      isAllDay,
      selectedPriority,
      isSomeday,
      selectedWorkItemTypeId,
      selectedLabelIds,
      createTask,
      createTaskLabel,
      createTaskContent,
      labels,
      labelGroups,
      t,
    ],
  )

  const handleReorder = useCallback(
    (event: TaskReorderEvent) => {
      const items = displayTasks
      if (items.length <= 1) {
        return
      }

      const sourceIndex = items.findIndex(
        (task) => task.id === event.sourceId,
      )
      if (sourceIndex === -1) {
        return
      }

      const reordered = [...items]
      const [moved] = reordered.splice(sourceIndex, 1)
      // `splice` returns `T[]`, so destructuring gives `T | undefined`. Bail
      // rather than reordering against a phantom item; this also lets every
      // `moved.*` read below narrow.
      if (!moved) return

      if (event.type === 'item') {
        const targetIndex = items.findIndex(
          (task) => task.id === event.targetId,
        )
        if (targetIndex === -1 || event.sourceId === event.targetId) {
          return
        }

        let insertIndex = targetIndex
        if (sourceIndex < targetIndex) {
          insertIndex -= 1
        }

        if (event.dropPosition === 'after') {
          insertIndex += 1
        }

        insertIndex = Math.max(0, Math.min(reordered.length, insertIndex))
        reordered.splice(insertIndex, 0, moved)
      } else {
        reordered.splice(reordered.length, 0, moved)
      }

      const hasChanged = reordered.some(
        (item, index) => item.id !== items[index]?.id,
      )
      if (!hasChanged) {
        return
      }

      const movedIndex = reordered.findIndex((task) => task.id === moved.id)
      const beforeSort =
        movedIndex > 0 ? (reordered[movedIndex - 1]?.sortOrder ?? null) : null
      const afterSort =
        movedIndex < reordered.length - 1
          ? (reordered[movedIndex + 1]?.sortOrder ?? null)
          : null

      const shouldRebalance =
        beforeSort !== null &&
        afterSort !== null &&
        compareRanks(beforeSort, afterSort) >= 0

      if (shouldRebalance) {
        rebalanceSortOrders(reordered, updateTask)
        return
      }

      const nextSortOrder = generateRankBetween(beforeSort, afterSort)
      if (nextSortOrder === moved.sortOrder) {
        return
      }

      try {
        void updateTask(moved.id, { sortOrder: nextSortOrder })
      } catch (error) {
        // Error updating task order
      }
    },
    [displayTasks, updateTask],
  )

  const handleTaskSelect = useCallback(
    (task: TaskDocument) => {
      // If this is a subtask (has a parentId) and navigateToSubtasks is enabled, navigate to the task detail page
      if (task.parentId && workspaceSlug && navigateToSubtasks) {
        const pathname = routerState.location.pathname
        let source: 'tasks' | 'today' | 'upcoming' | 'someday' = 'tasks'

        if (pathname.includes('/tasks/today')) {
          source = 'today'
        } else if (pathname.includes('/tasks/upcoming')) {
          source = 'upcoming'
        } else if (pathname.includes('/tasks/someday')) {
          source = 'someday'
        }

        const navState: Record<string, unknown> = { from: source }

        navigate({
          to: '/$workspaceSlug/tasks/$taskId',
          params: { workspaceSlug, taskId: task.id },
          state: navState,
        })
        return
      }

      // Default behavior: update selected task (refresh panel/dialog content)
      setSelectedTaskId(task.id)
      // In simple view mode or mobile split view, open the dialog when a task is selected
      if (viewMode === 'simple' || (isMobile && viewMode === 'split')) {
        setIsDialogOpen(true)
      }
    },
    [viewMode, isMobile, workspaceSlug, routerState, navigate, navigateToSubtasks],
  )

  const handleSubTaskClick = useCallback(
    (subTaskId: string) => {
      // If navigateToSubtasks is enabled, navigate to the subtask's detail page (URL change)
      if (navigateToSubtasks && workspaceSlug) {
        const pathname = routerState.location.pathname
        let source: 'tasks' | 'today' | 'upcoming' | 'someday' = 'tasks'

        if (pathname.includes('/tasks/today')) {
          source = 'today'
        } else if (pathname.includes('/tasks/upcoming')) {
          source = 'upcoming'
        } else if (pathname.includes('/tasks/someday')) {
          source = 'someday'
        }

        const navState: Record<string, unknown> = { from: source }

        navigate({
          to: '/$workspaceSlug/tasks/$taskId',
          params: { workspaceSlug, taskId: subTaskId },
          state: navState,
        })
        return
      }

      // Otherwise, just update the selected task (refresh panel/dialog content with animation)
      setSelectedTaskId(subTaskId)
    },
    [navigateToSubtasks, workspaceSlug, routerState, navigate],
  )

  const handleParentTaskClick = useCallback((parentTaskId: string) => {
    // Update the selected task to the parent task
    setSelectedTaskId(parentTaskId)
  }, [])

  // Close dialog when view mode changes from simple to split
  useEffect(() => {
    if (viewMode === 'split') {
      setIsDialogOpen(false)
    }
  }, [viewMode])

  const taskViewClassName = 'flex-1 min-h-0'

  const openAddTask = useCallback((preset: { dueDate?: Date | null; statusId?: string }) => {
    if (preset.dueDate !== undefined) {
      setDueDate(preset.dueDate ?? undefined)
      if (preset.dueDate) setIsSomeday(false)
    }
    if (preset.statusId) setSelectedStatusId(preset.statusId)
    setIsAddTaskExpanded(true)
  }, [])

  const taskViewContext: TaskViewContext = {
    items: displayTasks,
    isLoading: isTaskListLoading,
    defaultOnReorder: handleReorder,
    onSelect: handleTaskSelect,
    selectedTaskId,
    containerClassName: taskViewClassName,
    containerStyle: undefined,
    itemClassName: undefined,
    formatDueDate: formatTaskDueDate,
    viewMode,
    openAddTask: showAddTaskInput ? openAddTask : undefined,
  }

  const taskViewContent = renderTaskView ? (
    renderTaskView(taskViewContext)
  ) : (
    <DefaultTaskListView {...taskViewContext} />
  )

  // Shared task list content
  const taskListContent = (
    // `overflow-hidden`, not `overflow-x-hidden`: hiding only one axis makes
    // the other compute to `auto`, which would turn this into a second
    // vertical scroll container stacked on the list's own.
    <div className="flex flex-1 min-h-0 min-w-0 flex-col overflow-hidden">
      <div className="flex-shrink-0">
        {pageTitle && (
          // One bar for title, count, display and "+ Task" (design 18a).
          <div className={cn('flex h-[54px] flex-shrink-0 items-center gap-2 px-4', viewMode === 'split' ? 'md:px-5' : 'md:px-8')}>
            <PhoneMenuButton />
            <h1 className={cn('min-w-0 truncate font-semibold text-foreground', viewMode === 'split' ? 'text-[17px]' : 'text-xl')}>
              {pageTitle}
            </h1>
            <span className="shrink-0 text-[13px] tabular-nums text-muted-foreground">{headerCount ?? displayTasks.length}</span>
            <div className="ml-auto flex items-center gap-1.5">
              {headerActions}
              {showViewModeToggle && <LayoutButton scope={{ kind: 'taskPages' }} />}
              {showAddTaskInput && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddTaskClick}
                  aria-label={t('quickAdd.addTask')}
                  aria-keyshortcuts="c"
                  data-testid="task-add-trigger"
                  className="h-8 gap-1.5 rounded-lg px-3 text-[13px] phone:hidden"
                >
                  <Plus className="size-3.5" />
                  {t('quickAdd.task')}
                  <kbd className="ml-0.5 rounded bg-white/15 px-1 font-mono text-[10.5px] opacity-80">C</kbd>
                </Button>
              )}
            </div>
          </div>
        )}
        {showEmptyIntro && (
          <div className="@container">
            <div className={cn('mx-3 px-1 pb-3 @2xl:mx-4', pageTitle ? 'pt-2' : 'pt-10')}>
              <h2 className="text-[17px] font-semibold text-foreground">{emptyIntro.title}</h2>
              <p className="mt-1 max-w-xl text-[13px] text-muted-foreground">{emptyIntro.description}</p>
            </div>
          </div>
        )}
        {showAddTaskInput && (!pageTitle || isAddTaskExpanded) && (
        // The open row lines up with the list's cards (same container
        // breakpoint and inset as TaskList), since that's where it lands.
        <div
          className={cn(
            'flex-shrink-0 pb-3',
            isAddTaskExpanded
              ? '@container'
              : cn('px-4', viewMode === 'split' ? 'md:px-5' : 'md:px-8'),
            !pageTitle && !showEmptyIntro && 'pt-8',
          )}
        >
          {!isAddTaskExpanded ? (
            <Button
              type="button"
              variant="ghost"
              onClick={handleAddTaskClick}
              disabled={isTaskListLoading}
              className="group h-auto gap-2 rounded-full px-3 py-1.5 text-sm font-medium text-muted-foreground/70 bg-transparent hover:bg-transparent focus-visible:bg-transparent active:bg-transparent hover:text-foreground"
            >
              <span className="flex size-6 items-center justify-center rounded-full transition-colors group-hover:bg-accent group-hover:text-foreground">
                <Plus className="size-4" />
              </span>
              <span className="transition-colors group-hover:text-foreground">
                {t('quickAdd.addTask')}
              </span>
            </Button>
          ) : (
            // Quick add as a row (design 25a): shaped like the task it will
            // become, at the top of the list where the task lands.
            <AddTaskInput
              key={formKey}
              ref={taskInputElementRef}
              className="px-3 @2xl:px-4"
              variant="row"
              placeholder={t('quickAdd.namePlaceholder')}
              descriptionPlaceholder={t('quickAdd.descriptionPlaceholder')}
              showDescription={true}
              showProject={true}
              enableNaturalLanguageDate={false}
              nameValue={taskName}
              onNameChange={handleTaskNameChange}
              parsedCount={parsedCount}
              descriptionValue={description}
              onDescriptionChange={setDescription}
              startDate={startDate}
              dueDate={dueDate}
              onStartDateChange={(date) =>
                handleDatePickerChange('start', date)
              }
              onDueDateChange={(date) =>
                handleDatePickerChange('due', date)
              }
              reminderOffsetMinutes={reminderOffsetMinutes}
              onReminderChange={setReminderOffsetMinutes}
              recurrencePattern={recurrencePattern}
              onRecurrenceChange={setRecurrencePattern}
              projects={projects}
              selectedProjectId={selectedProjectId}
              defaultProjectId={resolvedDefaultProjectId}
              onProjectChange={setSelectedProjectId}
              disableProjectSelection={!!projectId}
              selectedPriority={selectedPriority}
              onPriorityChange={setSelectedPriority}
              selectedUserId={selectedUserId}
              onUserChange={setSelectedUserId}
              statuses={statusList}
              selectedStatusId={selectedStatusId}
              onStatusChange={setSelectedStatusId}
              availableLabels={labels}
              labelGroups={labelGroups}
              selectedLabelIds={selectedLabelIds}
              onLabelsChange={setSelectedLabelIds}
              selectedFiles={selectedFiles}
              onFilesChange={setSelectedFiles}
              isSomeday={isSomeday}
              onSomedayChange={setIsSomeday}
              isAllDay={isAllDay}
              onIsAllDayChange={setIsAllDay}
              onCreateClick={() => void createTaskHandler()}
              onCreateAndOpenClick={() => void createTaskHandler({ open: true })}
              onCancelClick={handleCancelAddTask}
              onPasteLines={(lines) => void createTasksFromLines(lines)}
              statusMode={statusMode}
              selectedWorkItemTypeId={selectedWorkItemTypeId}
              onWorkItemTypeChange={isWorkItemTypeEnabled ? setSelectedWorkItemTypeId : undefined}
              workItemTypeCount={isWorkItemTypeEnabled ? projectWorkItemTypes.length : 0}
            />
          )}
        </div>
        )}
      </div>
      {showEmptyIntro
        ? emptyIntro.renderTips || emptyIntro.footer
          ? (
            <div className="@container flex-shrink-0">
              <div className="mx-3 flex flex-col gap-3 px-1 @2xl:mx-4">
                {emptyIntro.renderTips?.({ openAddTask: handleAddTaskClick })}
                {emptyIntro.footer && <p className="text-xs text-muted-foreground">{emptyIntro.footer}</p>}
              </div>
            </div>
          )
          : null
        : isEmpty && renderEmptyState
          ? renderEmptyState({ openAddTask: handleAddTaskClick })
          : taskViewContent}
    </div>
  )

  // On mobile in split view, use Sheet modal instead of split panels.
  // Bind height with h-full (+ flex-1): without a bounded height ListDetailsView
  // grows with content and overflow-hidden ancestors clip it, so touch scroll
  // dies on iPhone Safari (MOL-37).
  if (isMobile && viewMode === 'split') {
    return (
      <div className="flex h-full min-h-0 w-full overflow-hidden">
        <motion.div
          key="mobile-split-view"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="flex h-full min-h-0 w-full flex-1 justify-center overflow-hidden"
        >
          <div className={cn('flex h-full min-h-0 w-full min-w-0 flex-col', !wide && 'max-w-4xl')}>
            {taskListContent}
          </div>
          <TaskDetailsDialog
            task={selectedTask}
            statuses={statusList}
            parentTask={selectedParentTask}
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            onSubTaskClick={handleSubTaskClick}
            onParentTaskClick={handleParentTaskClick}
            taskIdentifier={taskIdentifier}
            onNavigateUp={handleNavigateUp}
            onNavigateDown={handleNavigateDown}
            canNavigateUp={canNavigateUp}
            canNavigateDown={canNavigateDown}
            position={selectedPosition}
          />
        </motion.div>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full">
      <AnimatePresence mode="wait">
        {viewMode === 'split' ? (
          // Split view mode - two panel layout (desktop only)
          <motion.div
            key="split-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 min-h-0 w-full h-full"
          >
            <ResizablePanelGroup
              // Remounting on the breakpoint crossing is what lets the panels
              // pick up the new default; sizes are only read on mount.
              key={isCompactDesktop ? 'compact' : 'wide'}
              orientation="horizontal"
              className="h-full w-full"
            >
              {/* Sizes must carry explicit units — react-resizable-panels v4
                  reads bare numbers as pixels, not percentages. On laptop-sized
                  windows the list takes a larger share so row titles have room
                  to render without truncating (MOL-11).

                  className and style both land on the panel's inner content
                  div, which the library ships as an inline `overflow: auto`.
                  Left alone that is a second scroll container wrapping the
                  list's own, so each panel turns it off — via `style`, since
                  the library's inline rule would outrank a utility class — and
                  hands its height down the flex chain instead. */}
              <ResizablePanel
                defaultSize={isCompactDesktop ? '45%' : '35%'}
                minSize="30%"
                className="flex h-full min-h-0 flex-col"
                style={{ overflow: 'hidden' }}
              >
                {taskListContent}
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel
                defaultSize={isCompactDesktop ? '55%' : '65%'}
                minSize="35%"
                className="flex h-full min-h-0 flex-col bg-card"
                style={{ overflow: 'hidden' }}
              >
                <TaskDetailsPanel
                  task={selectedTask}
                  parentTask={selectedParentTask}
                  statuses={statusList}
                  onSubTaskClick={handleSubTaskClick}
                  onParentTaskClick={handleParentTaskClick}
                  position={selectedPosition}
                  onNavigateUp={handleNavigateUp}
                  onNavigateDown={handleNavigateDown}
                  canNavigateUp={canNavigateUp}
                  canNavigateDown={canNavigateDown}
                  onClose={() => setSelectedTaskId(null)}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          </motion.div>
        ) : (
          // Simple view mode - centered content with max width
          <motion.div
            key="simple-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex h-full min-h-0 w-full flex-1 justify-center overflow-hidden"
          >
            <div className={cn('flex h-full min-h-0 w-full flex-col', !wide && 'max-w-4xl')}>
              {taskListContent}
            </div>
            <TaskDetailsDialog
              task={selectedTask}
              statuses={statusList}
              parentTask={selectedParentTask}
              open={isDialogOpen}
              onOpenChange={setIsDialogOpen}
              onSubTaskClick={handleSubTaskClick}
              onParentTaskClick={handleParentTaskClick}
              taskIdentifier={taskIdentifier}
              onNavigateUp={handleNavigateUp}
              onNavigateDown={handleNavigateDown}
              canNavigateUp={canNavigateUp}
              canNavigateDown={canNavigateDown}
              position={selectedPosition}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function DefaultTaskListView({
  items,
  isLoading,
  defaultOnReorder,
  onSelect,
  selectedTaskId,
  containerClassName,
  containerStyle,
  itemClassName,
  formatDueDate,
  viewMode,
}: TaskViewContext) {
  return (
    <TaskList
      className={containerClassName}
      style={containerStyle}
      items={items}
      onReorder={defaultOnReorder}
      onSelect={onSelect}
      selectedTaskId={selectedTaskId}
      isLoading={isLoading}
      itemClassName={itemClassName}
      formatDueDate={formatDueDate}
      viewMode={viewMode}
    />
  )
}
